import './instrument';

// Import ./instrument.ts before any other imports
import { Auth0ProviderWithRedirect, Auth0Region, AuthProvider } from '@ocula/auth';
import { BroadcastChannelProvider } from '@ocula/broadcast-channel';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Provider as JotaiProvider } from 'jotai';
import { HelmetProvider } from 'react-helmet-async';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';

import { AppErrorBoundary } from '@src/components/ErrorState/AppErrorBoundary';
import { ServiceWorkerUpdater } from '@src/components/ServiceWorkerUpdater';
import { GlobalStyle } from '@src/theme/GlobalStyle';

import { AppRouting } from './AppRouting';
import LandingPage from './pages/Region/LandingPage';

import '@ocula/ui/src/styles/globals/codeElements.css';
import '@ocula/ui/src/styles/globals/collapsingMargins.css';
import '@ocula/ui/src/styles/globals/fontFace.css';
import '@src/theme/theme.css';

const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const FIVE_MINS_MS = 5 * 60 * 1000;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      cacheTime: ONE_DAY_MS,
      staleTime: FIVE_MINS_MS,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  },
  logger: {
    log: console.log,
    warn: console.warn,
    error: console.error,
  },
});

export const AppWithAuth0Region = () => {
  const { region } = useParams();

  if (!region || !(region && Object.values(Auth0Region).includes(region as Auth0Region))) {
    return <Navigate to="/" />;
  }
  // redirect to region in path
  return (
    <Auth0ProviderWithRedirect region={region as Auth0Region}>
      <AuthProvider>
        <AppRouting />
      </AuthProvider>
    </Auth0ProviderWithRedirect>
  );
};

export function App() {
  return (
    <AppErrorBoundary>
      <GlobalStyle />
      <JotaiProvider>
        <QueryClientProvider client={queryClient}>
          <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
          <BroadcastChannelProvider>
            <HelmetProvider>
              <Routes>
                <Route path="/:region/*" element={<AppWithAuth0Region />} />
                <Route path="/*" element={<LandingPage />} />
              </Routes>
              <ServiceWorkerUpdater />
            </HelmetProvider>
          </BroadcastChannelProvider>
        </QueryClientProvider>
      </JotaiProvider>
    </AppErrorBoundary>
  );
}
